import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '@/components/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { getWhopMembershipByEmail } from '@/services/whopPlanService';
import { supabase } from '@/lib/supabase';

/**
 * UGC-specific Whop purchase success page
 * Handles post-checkout redirect for UGC purchases
 * Ensures user is logged in and routes to UGC onboarding/dashboard
 */
const WhopUGCSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your UGC purchase...');

  useEffect(() => {
    const handleSuccess = async () => {
      try {
        // Get email from URL params (Whop should pass this)
        let whopEmail = searchParams.get('email');
        
        // Clean up email if it has template brackets (for testing)
        if (whopEmail && whopEmail.includes('{{')) {
          whopEmail = whopEmail.replace(/\{\{|\}\}/g, '').trim();
        }
        
        // Wait for auth to be ready
        if (!user) {
          setStatus('processing');
          setMessage('Create your account to access your UGC purchase...');
          
          // If Whop provided an email, pre-fill registration
          const registerUrl = whopEmail 
            ? `/ugc-authentication?mode=register&email=${encodeURIComponent(whopEmail)}&returnUrl=/whop-ugc-success`
            : `/ugc-authentication?mode=register&returnUrl=/whop-ugc-success`;
          
          // Redirect to UGC authentication (separate from coaching auth)
          setTimeout(() => navigate(registerUrl), 2000);
          return;
        }

        // First check if profile already has UGC status (from webhook)
        setMessage('Verifying your purchase...');
        const { data: latestProfile } = await supabase
          .from('user_profiles')
          .select('ugc_status, ugc_tier, ugc_membership_id, whop_user_id')
          .eq('id', user.id)
          .single();

        // If webhook already processed this, we're good
        if (latestProfile?.ugc_status === 'active') {
          setMessage('Purchase verified! Setting up your account...');
          // Profile already has UGC data from webhook
        } else {
          // Webhook might not have fired yet, try Whop API
          console.log('Checking Whop API for membership...');
          const membership = await getWhopMembershipByEmail(user.email);

          if (!membership || membership.product !== 'ugc') {
            console.warn('UGC membership not found in Whop API');
            setStatus('error');
            setMessage('We couldn\'t verify your UGC purchase. Please contact support.');
            setTimeout(() => navigate('/app/dashboard'), 3000);
            return;
          }

          // Update profile with UGC info from Whop API
          setMessage('Activating your UGC account...');
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({
              ugc_tier: membership.tier || 'creator',
              ugc_status: 'active',
              ugc_membership_id: membership.membershipId,
              whop_user_id: membership.userId
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Failed to update profile:', updateError);
            // Don't block on this - continue anyway
          }
        }

        if (updateError) {
          console.error('Failed to update profile:', updateError);
          // Don't block on this - continue anyway
        }

        // Check if they've completed UGC onboarding
        if (profile?.ugc_onboarding_completed) {
          setStatus('success');
          setMessage('Welcome back! Taking you to your UGC dashboard...');
          setTimeout(() => navigate('/app/ugc-dashboard'), 2000);
        } else {
          setStatus('success');
          setMessage('Purchase complete! Let\'s set up your UGC profile...');
          setTimeout(() => navigate('/app/ugc-onboarding'), 2000);
        }
      } catch (error) {
        console.error('UGC success handler error:', error);
        setStatus('error');
        setMessage('Something went wrong. Redirecting...');
        setTimeout(() => navigate('/app/dashboard'), 3000);
      }
    };

    handleSuccess();
  }, [user, profile, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Almost There!</h2>
            <p className="text-white/70 mb-4">{message}</p>
            {!user && (
              <p className="text-sm text-white/50">
                Don't have an account yet? No problem! We'll create one for you.
              </p>
            )}
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-6">
              <Icon name="Check" size={32} className="text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Purchase Confirmed!</h2>
            <p className="text-white/70 mb-4">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-6">
              <Icon name="AlertCircle" size={32} className="text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-white/70 mb-6">{message}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/app/dashboard')}
                className="w-full px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WhopUGCSuccess;
